import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiCall } from '../utils/api';
import useUserPermissions from '../hooks/useUserPermissions';

function RunbookList() {
    const navigate = useNavigate();
    const { canModify } = useUserPermissions();
    const [runbooks, setRunbooks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedRunbookId, setSelectedRunbookId] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const rowsPerPage = 10;

    const fetchRunbooks = async () => {
        try {
            setLoading(true);
            setError(null);
            
            // Use the apiCall utility which handles authentication
            const data = await apiCall('/runbooks');
            setRunbooks(data);
        } catch (error) {
            console.error("Could not fetch runbooks:", error);
            setError(error.message || "Failed to fetch runbooks");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRunbooks();
    }, []);

    const handleDeleteRunbook = async () => {
        if (!selectedRunbookId || !window.confirm(`Are you sure you want to delete runbook ${selectedRunbookId}?`)) {
            return;
        }
        
        try {
            // Use apiCall for DELETE request
            await apiCall(`/runbooks/${selectedRunbookId}`, {
                method: 'DELETE'
            });
            
            alert('Runbook deleted successfully!');
            setSelectedRunbookId(null);
            fetchRunbooks(); // Refresh the list
        } catch (err) {
            console.error("Failed to delete runbook:", err);
            alert(`Failed to delete runbook: ${err.message}`);
        }
    };

    const handleCheckboxChange = (runbookId) => {
        setSelectedRunbookId(prev => (prev === runbookId ? null : runbookId));
    };

    const handleCreateRunbook = () => navigate('/create-runbook');
    
    const handleEditRunbook = () => { 
        if (selectedRunbookId) {
            navigate(`/edit-runbook/${selectedRunbookId}`); 
        }
    };
    
    const handleViewRunbook = (e, runbookId) => {
        e.preventDefault();
        navigate(`/view-runbook/${runbookId}`);
    };

    const totalPages = Math.ceil(runbooks.length / rowsPerPage);
    const currentRunbooks = runbooks.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

    if (loading) {
        return (
            <div className="container-fluid mt-4">
                <div className="d-flex justify-content-center">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading runbooks...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container-fluid mt-4">
                <div className="alert alert-danger" role="alert">
                    <h4 className="alert-heading">Error!</h4>
                    <p>{error}</p>
                    <button className="btn btn-outline-danger" onClick={fetchRunbooks}>
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid mt-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h2>Runbooks</h2>
                    {/* {isReadOnly && (
                        <small className="text-muted">Viewing as: {getUserRole()} (Read-only access)</small>
                    )} */}
                </div>
                <div className="d-flex gap-2">
                    {canModify() && (
                        <button onClick={handleCreateRunbook} className="btn btn-primary">
                            Create New Runbook
                        </button>
                    )}
                    <button onClick={() => navigate('/import-runbook')} className="btn btn-success">
                        <i className="bi bi-file-earmark-excel me-1"></i>
                        Import Runbook
                    </button>
                </div>
            </div>

            <div className="table-responsive">
                <table className="table table-bordered table-hover">
                    <thead className="table-dark">
                        <tr>
                            {canModify() && <th style={{width: '50px'}}></th>}
                            <th>Runbook ID</th>
                            <th style={{ textAlign: 'left' }}>Runbook Title</th>
                            <th>Tribe</th>
                            <th style={{ textAlign: 'left' }}>Solution</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentRunbooks.length > 0 ? (
                            currentRunbooks.map((runbook) => (
                                <tr key={runbook['Runbook ID']} className={selectedRunbookId === runbook['Runbook ID'] ? 'table-active' : ''}>
                                    {canModify() && (
                                        <td style={{ verticalAlign: 'middle', textAlign: 'center' }}>
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                checked={selectedRunbookId === runbook['Runbook ID']}
                                                onChange={() => handleCheckboxChange(runbook['Runbook ID'])}
                                            />
                                        </td>
                                    )}
                                    <td style={{ verticalAlign: 'middle' }}>
                                        <a 
                                            href={`/view-runbook/${runbook['Runbook ID']}`} 
                                            onClick={(e) => handleViewRunbook(e, runbook['Runbook ID'])}
                                            className="text-decoration-none"
                                        >
                                            {runbook['Runbook ID']}
                                        </a>
                                    </td>
                                    <td style={{ verticalAlign: 'middle', textAlign: 'left' }}>{runbook['Runbook Title']}</td>
                                    <td style={{ verticalAlign: 'middle' }}>{runbook.Tribe}</td>
                                    <td style={{ verticalAlign: 'middle', textAlign: 'left' }}>{runbook.Solution}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={canModify() ? "5" : "4"} className="text-center py-4">
                                    <div className="text-muted">
                                        <p>No runbooks available.</p>
                                        {canModify() && (
                                            <button onClick={handleCreateRunbook} className="btn btn-primary btn-sm">
                                                Create Your First Runbook
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {totalPages > 1 && (
                <nav aria-label="Runbook pagination">
                    <div className="d-flex justify-content-center align-items-center mt-4">
                        <button 
                            className="btn btn-outline-secondary me-2" 
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))} 
                            disabled={currentPage === 1}
                        >
                            Previous
                        </button>
                        <span className="mx-3">
                            Page {currentPage} of {totalPages} ({runbooks.length} total runbooks)
                        </span>
                        <button 
                            className="btn btn-outline-secondary ms-2" 
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} 
                            disabled={currentPage === totalPages}
                        >
                            Next
                        </button>
                    </div>
                </nav>
            )}

            {selectedRunbookId && canModify() && (
                <div className="d-flex justify-content-end gap-2 mt-4">
                    <button 
                        onClick={handleEditRunbook} 
                        className="btn btn-warning"
                        title={`Edit runbook ${selectedRunbookId}`}
                    >
                        Edit Selected
                    </button>
                    <button 
                        onClick={handleDeleteRunbook} 
                        className="btn btn-danger"
                        title={`Delete runbook ${selectedRunbookId}`}
                    >
                        Delete Selected
                    </button>
                </div>
            )}
        </div>
    );
}

export default RunbookList;